import { assignTokens } from '@sail/engine';
import { tokens } from '@sail/ui';
import { calculateFontSizeRatio, getScaledFontSize, parseFontSizeUnit } from './tokenUtils';
import { themeValueDefaults } from './appearanceOptionsDefaults';
import { getAccessibleColorFromBackgroundValue, getAccessibleTextColor, getAccessibleBorderColor, getAccessibleColor, deriveColorFromValue } from './contrastUtils';
import { getBrand700Color, getFormTextHoverColor, getColor } from './utils';
export const getFontFamily = (theme)=>theme.fontFamily;
// Background Colors
export const getColorBackground = (theme)=>theme.colorBackground;
export const getColorBackgroundForCalculationsFromValue = (val)=>val || themeValueDefaults.colorBackground;
export const getColorBackgroundForCalculations = (theme)=>getColorBackgroundForCalculationsFromValue(getColorBackground(theme));
export const getAccessibleColorFromBackground = (theme, darkContrast, lightContrast)=>{
    const backgroundColor = getColorBackgroundForCalculations(theme);
    return getAccessibleColorFromBackgroundValue(backgroundColor, darkContrast, lightContrast);
};
// Primary Button
export const getPrimaryColor = (theme)=>{
    var _theme_colorPrimary;
    return (_theme_colorPrimary = theme.colorPrimary) !== null && _theme_colorPrimary !== void 0 ? _theme_colorPrimary : themeValueDefaults.colorPrimary;
};
export const getPrimaryColorHovered = (theme)=>deriveColorFromValue(getPrimaryColor(theme), [
        900,
        850
    ], [
        0,
        150
    ]);
export const getButtonPrimaryColorBackground = (theme)=>theme.buttonPrimaryColorBackground || getPrimaryColor(theme);
export const getButtonPrimaryColorBorder = (theme)=>theme.buttonPrimaryColorBorder || getAccessibleBorderColor(theme.buttonPrimaryColorBackground || getPrimaryColor(theme), theme);
export const getButtonPrimaryColorText = (theme)=>theme.buttonPrimaryColorText || getAccessibleTextColor(getButtonPrimaryColorBackground(theme));
export const getColorPrimaryButtonIcon = (theme)=>getAccessibleColor(getButtonPrimaryColorText(theme), [
        150,
        0
    ]);
export const getButtonPrimaryColorBackgroundPressed = (theme)=>deriveColorFromValue(getButtonPrimaryColorBackground(theme), [
        900,
        850
    ], [
        0,
        150
    ]);
export const getButtonPrimaryColorBorderHovered = (theme)=>{
    const borderColor = getButtonPrimaryColorBorder(theme);
    return borderColor && getBrand700Color(borderColor);
};
export const getButtonPrimaryColorBorderPressed = (theme)=>getButtonPrimaryColorBorderHovered(theme);
// Secondary Button
export const getButtonSecondaryColorBackground = (theme)=>theme.buttonSecondaryColorBackground || themeValueDefaults.buttonSecondaryColorBackground;
export const getButtonSecondaryColorBorder = (theme)=>theme.buttonSecondaryColorBorder || getButtonSecondaryColorBackground(theme);
export const getButtonSecondaryColorText = (theme)=>theme.buttonSecondaryColorText || getAccessibleTextColor(getButtonSecondaryColorBackground(theme));
export const getColorSecondaryButtonIcon = (theme)=>getAccessibleColor(getButtonSecondaryColorText(theme), [
        150,
        0
    ]);
export const getButtonSecondaryColorBackgroundPressed = (theme)=>deriveColorFromValue(getButtonSecondaryColorBackground(theme), [
        900,
        850
    ], [
        0,
        50
    ]);
export const getButtonSecondaryColorBorderHovered = (theme)=>getAccessibleColor(getButtonSecondaryColorBorder(theme), [
        0,
        150
    ]);
// Primary Link
export const getActionPrimaryColorText = (theme)=>theme.actionPrimaryColorText || getPrimaryColor(theme);
export const getActionPrimaryColorTextHover = (theme)=>{
    return deriveColorFromValue(getActionPrimaryColorText(theme), [
        900,
        850
    ], [
        0,
        150
    ]);
};
export const getColorPrimaryLinkIcon = (theme)=>theme.actionPrimaryColorText || getPrimaryColor(theme);
export const getColorPrimaryLinkIconHovered = (theme)=>{
    return deriveColorFromValue(getColorPrimaryLinkIcon(theme), [
        900,
        850
    ], [
        0,
        150
    ]);
};
export const getActionPrimaryTextDecorationColor = (theme)=>theme.actionPrimaryTextDecorationColor || getActionPrimaryColorText(theme);
export const getActionPrimaryTextDecorationColorHovered = (theme)=>deriveColorFromValue(theme.actionPrimaryTextDecorationColor, [
        900,
        850
    ], [
        0,
        150
    ]);
// Secondary Link
export const getActionSecondaryColorText = (theme)=>theme.actionSecondaryColorText || // SecondaryLinkText in sail-next is neutral700, #414552: https://sail.stripe.me/beta/tokens/all/
    getAccessibleColorFromBackground(theme, [
        900,
        200
    ], [
        0,
        700
    ]);
export const getColorSecondaryLinkIcon = (theme)=>getActionSecondaryColorText(theme);
export const getActionSecondaryColorTextHovered = (theme)=>{
    return deriveColorFromValue(theme.actionSecondaryColorText, [
        900,
        850
    ], [
        0,
        150
    ]);
};
export const getActionSecondaryTextDecorationColor = (theme)=>{
    return theme.actionSecondaryTextDecorationColor || getActionSecondaryColorText(theme);
};
export const getActionSecondaryTextDecorationColorHovered = (theme)=>deriveColorFromValue(theme.actionSecondaryTextDecorationColor, [
        900,
        850
    ], [
        0,
        150
    ]);
// Border Colors
export const getColorBorder = (theme)=>theme.colorBorder || // ColorBorder in sail-next is neutral150, #d5dbe1: https://sail.stripe.me/beta/tokens/all/
    getAccessibleColorFromBackground(theme, [
        900,
        750
    ], [
        0,
        150
    ]);
export const getFormHighlightColorBorder = (theme)=>theme.formHighlightColorBorder || getAccessibleBorderColor(getPrimaryColor(theme), theme, true) || getAccessibleColorFromBackground(theme, [
        900,
        600
    ], [
        0,
        300
    ]);
export const getOffsetBackgroundColor = (theme)=>theme.offsetBackgroundColor || // OffsetBackground in sail-next is neutral50, #f6f8fa: https://sail.stripe.me/beta/tokens/all/
    getAccessibleColorFromBackground(theme, [
        900,
        850
    ], [
        0,
        50
    ]);
export const getFormBackgroundColor = (theme)=>theme.formBackgroundColor || getColorBackground(theme) || themeValueDefaults.formBackgroundColor;
// Text Colors
export const getColorText = (theme)=>theme.colorText || getAccessibleTextColor(getColorBackgroundForCalculations(theme));
export const getColorFormText = (theme)=>theme.colorText || getAccessibleTextColor(getFormBackgroundColor(theme));
// Secondary Text Colors
export const getColorSecondaryText = (theme)=>theme.colorSecondaryText || // SecondaryText in sail-next is neutral500, #687385: https://sail.stripe.me/beta/tokens/all/
    getAccessibleColorFromBackground(theme, [
        900,
        400
    ], [
        0,
        500
    ]);
export const getColorFormDisabledBackground = (theme)=>// Form (Disabled) background in sail-next is neutral50, #f6f8fa: https://sail.stripe.me/beta/tokens/color/#forms
    getAccessibleColorFromBackground(theme, [
        900,
        850
    ], [
        0,
        50
    ]);
export const getColorOnFormDisabledBackgroundAccent = (theme)=>// onForm (Disabled) background accent in sail-next is neutral25
    getAccessibleColorFromBackground(theme, [
        900,
        875
    ], [
        0,
        25
    ]);
export const getColorFormDisabledBackgroundAccent = (theme)=>// Form (Disabled) background accent in sail-next is neutral100, #e9edf2: https://sail.stripe.me/beta/tokens/color/#forms
    getAccessibleColorFromBackground(theme, [
        900,
        800
    ], [
        0,
        100
    ]);
export const getColorFormDisabledText = (theme)=>// Form (Disabled) text in sail-next is neutral300, #a3acba: https://sail.stripe.me/beta/tokens/color/#forms
    getAccessibleColorFromBackground(theme, [
        900,
        600
    ], [
        0,
        300
    ]);
// Form Colors
export const getFormAccentColor = (theme)=>theme.formAccentColor || getPrimaryColor(theme);
export const getColorFormTextHover = (theme)=>{
    const textColor = getColorFormText(theme);
    return textColor && getFormTextHoverColor(textColor);
};
export const getColorFormPlaceholder = (theme)=>{
    const backgroundColor = getFormBackgroundColor(theme);
    // since form has an independent background color, use that for placeholder text generation
    return getAccessibleColorFromBackgroundValue(backgroundColor, [
        900,
        600
    ], [
        0,
        300
    ]);
};
// Badges
export const getBadgeDangerColorText = (theme)=>theme.badgeDangerColorText || themeValueDefaults.badgeDangerColorText;
export const getBadgeDangerColorBackground = (theme)=>theme.badgeDangerColorBackground || getAccessibleColor(getBadgeDangerColorText(theme), 500);
export const getBadgeDangerColorBorder = (theme)=>theme.badgeDangerColorBorder || getAccessibleColor(getBadgeDangerColorText(theme), 300);
export const getBadgeNeutralColorText = (theme)=>theme.badgeNeutralColorText || themeValueDefaults.badgeNeutralColorText;
export const getBadgeNeutralColorBorder = (theme)=>theme.badgeNeutralColorBorder || getAccessibleColor(getBadgeNeutralColorText(theme), 300);
export const getBadgeNeutralColorBackground = (theme)=>theme.badgeNeutralColorBackground || getAccessibleColor(getBadgeNeutralColorText(theme), 500);
export const getBadgeSuccessColorText = (theme)=>theme.badgeSuccessColorText || themeValueDefaults.badgeSuccessColorText;
export const getBadgeSuccessColorBorder = (theme)=>theme.badgeSuccessColorBorder || getAccessibleColor(getBadgeSuccessColorText(theme), 300);
export const getBadgeSuccessColorBackground = (theme)=>theme.badgeSuccessColorBackground || getAccessibleColor(getBadgeSuccessColorText(theme), 500);
export const getBadgeWarningColorText = (theme)=>theme.badgeWarningColorText || themeValueDefaults.badgeWarningColorText;
export const getBadgeWarningColorBorder = (theme)=>{
    if (theme.badgeWarningColorBorder) {
        return theme.badgeWarningColorBorder;
    }
    const accessibleColor = getAccessibleColor(getBadgeWarningColorText(theme), 300);
    const colorRgb = getColor(accessibleColor).rgb();
    colorRgb.b = Math.max(colorRgb.b - 46, 0); // Warning is special - we intentionally make the color less blue and more green, to go from orange to yellow
    colorRgb.g = Math.min(colorRgb.g + 13, 255);
    return colorRgb.formatHex();
};
export const getBadgeWarningColorBackground = (theme)=>{
    if (theme.badgeWarningColorBackground) {
        return theme.badgeWarningColorBackground;
    }
    const accessibleColor = getAccessibleColor(getBadgeWarningColorText(theme), 500);
    const colorRgb = getColor(accessibleColor).rgb();
    colorRgb.b = Math.max(colorRgb.b - 28, 0); // Warning is special - we intentionally make the color less blue and more green, to go from orange to yellow
    colorRgb.g = Math.min(colorRgb.g + 8, 255);
    return colorRgb.formatHex();
};
// Border Radius
export const getButtonBorderRadius = (theme)=>theme.buttonBorderRadius || theme.borderRadius || themeValueDefaults.buttonBorderRadius;
export const getFormBorderRadius = (theme)=>theme.formBorderRadius || theme.borderRadius || themeValueDefaults.formBorderRadius;
export const getBadgeBorderRadius = (theme)=>theme.badgeBorderRadius || theme.borderRadius || themeValueDefaults.badgeBorderRadius;
export const getOverlayBorderRadius = (theme)=>theme.overlayBorderRadius || theme.borderRadius || themeValueDefaults.overlayBorderRadius;
export const getOverlayBackdropColor = (theme)=>theme.overlayBackdropColor || `${getAccessibleColorFromBackground(theme, [
        900,
        700
    ], [
        0,
        200
    ])}B3`; // We calculate the backdrop color from the background, and append this number to achieve 70% opacity
// Shadows
export const getShadowMedium = (theme)=>{
    // Shadow medium in sail-next is "0px 3px 6px 0px rgba(0, 0, 0, 0.12), 0px 7px 14px 0px rgba(48, 49, 61, 0.08)"
    // https://sail.stripe.me/beta/tokens/shadow/
    // We calculate the backdrop color from the background, and append the hexcode 14 to achieve 70% opacity
    const neutral800Shadow = `${getAccessibleColorFromBackground(theme, [
        900,
        100
    ], [
        0,
        800
    ])}14`;
    // 12% opacity is set with hexcode 1F
    const darkShadow = `${getAccessibleColorFromBackground(theme, [
        900,
        0
    ], [
        0,
        900
    ])}1F`;
    return `0px 3px 6px 0px ${darkShadow}, 0px 7px 14px 0px ${neutral800Shadow}`;
};
// Font Size
const getFontSizeBase = (theme)=>theme.fontSizeBase || themeValueDefaults.fontSizeBase;
// Headings
export const getHeadingXlFontSize = (theme)=>theme.headingXlFontSize || getScaledFontSize(getFontSizeBase(theme), parseFontSizeUnit(getFontSizeBase(theme)), calculateFontSizeRatio(themeValueDefaults.fontSizeBase, themeValueDefaults.headingXlFontSize, 'px')) || themeValueDefaults.headingXlFontSize;
export const getHeadingLgFontSize = (theme)=>theme.headingLgFontSize || getScaledFontSize(getFontSizeBase(theme), parseFontSizeUnit(getFontSizeBase(theme)), calculateFontSizeRatio(themeValueDefaults.fontSizeBase, themeValueDefaults.headingLgFontSize, 'px')) || themeValueDefaults.headingLgFontSize;
export const getHeadingMdFontSize = (theme)=>theme.headingMdFontSize || getScaledFontSize(getFontSizeBase(theme), parseFontSizeUnit(getFontSizeBase(theme)), calculateFontSizeRatio(themeValueDefaults.fontSizeBase, themeValueDefaults.headingMdFontSize, 'px')) || themeValueDefaults.headingMdFontSize;
export const getHeadingSmFontSize = (theme)=>theme.headingSmFontSize || getScaledFontSize(getFontSizeBase(theme), parseFontSizeUnit(getFontSizeBase(theme)), calculateFontSizeRatio(themeValueDefaults.fontSizeBase, themeValueDefaults.headingSmFontSize, 'px')) || themeValueDefaults.headingSmFontSize;
export const getHeadingXsFontSize = (theme)=>theme.headingXsFontSize || getScaledFontSize(getFontSizeBase(theme), parseFontSizeUnit(getFontSizeBase(theme)), calculateFontSizeRatio(themeValueDefaults.fontSizeBase, themeValueDefaults.headingXsFontSize, 'px')) || themeValueDefaults.headingXsFontSize;
// Body
export const getBodyMdFontSize = (theme)=>theme.bodyMdFontSize || getScaledFontSize(getFontSizeBase(theme), parseFontSizeUnit(getFontSizeBase(theme)), calculateFontSizeRatio(themeValueDefaults.fontSizeBase, themeValueDefaults.bodyMdFontSize, 'px')) || themeValueDefaults.bodyMdFontSize;
export const getBodySmFontSize = (theme)=>theme.bodySmFontSize || getScaledFontSize(getFontSizeBase(theme), parseFontSizeUnit(getFontSizeBase(theme)), calculateFontSizeRatio(themeValueDefaults.fontSizeBase, themeValueDefaults.bodySmFontSize, 'px')) || themeValueDefaults.bodySmFontSize;
// Label
export const getLabelMdFontSize = (theme)=>theme.labelMdFontSize || getScaledFontSize(getFontSizeBase(theme), parseFontSizeUnit(getFontSizeBase(theme)), calculateFontSizeRatio(themeValueDefaults.fontSizeBase, themeValueDefaults.labelMdFontSize, 'px')) || themeValueDefaults.labelMdFontSize;
export const getLabelSmFontSize = (theme)=>theme.labelSmFontSize || getScaledFontSize(getFontSizeBase(theme), parseFontSizeUnit(getFontSizeBase(theme)), calculateFontSizeRatio(themeValueDefaults.fontSizeBase, themeValueDefaults.labelSmFontSize, 'px')) || themeValueDefaults.labelSmFontSize;
export const getFocusRingColor = (theme)=>{
    const primaryColor = getPrimaryColor(theme);
    const visibleFocusRingColor = getAccessibleBorderColor(primaryColor, theme, true);
    return `0 0 0 4px ${getColor(visibleFocusRingColor).copy({
        opacity: 0.25
    }).formatRgb()}`;
};
export const mobileSdkFocusRing = '0 0 0 0 transparent';
export const getMobileSdkInputFocusRingOverride = (theme, useMobileSdkTheme)=>{
    if (!useMobileSdkTheme) {
        return [];
    }
    return [
        assignTokens(tokens.focus, getFocusRingColor(theme))
    ];
};

//# sourceMappingURL=tokenOverrides.js.map